<script setup lang="ts">
  import { ref, computed, inject, watch, nextTick } from 'vue'
  import { Keyboard, Icon, Button, toast, Tooltip } from '@rapoo/element'
  import type {
    KeyboardConfig,
    KeyEventName,
    KeyboardKeyEventPayload,
    KeyConfig,
  } from '@rapoo/element'
  import type { DksSlotInfo, DksKeyInfo, DksContactState } from '@rapoo/device-sdk'
  import USlider from '../../TabDKS/components/USlider.vue'
  import { fullSize108 } from '@/utils/fullSize108'
  import { setDragData, DragType } from '@/utils/dnd'
  import { useLocaleStore } from '@/stores'
  import {
    SelectedKeySymbol,
    KeySettingsSymbol,
    KeyLayerSymbol,
    type KeyChangePayload,
  } from '../../../types'
  import {
    KEYCODE_KEYWORDS_MAP,
    getLocalizedField,
    getKeyConfigByTypeCode,
    buildKeySettingFromPayload,
  } from '../../../utils/keyMapSettings'
  import { GCOME_MATRIX_TYPE, keyConfigToGcomeTriple } from '../../../utils/advancedKeyMapSettings'
  import { findSettingByTargetKey, parseNumber } from '../../../utils/keyIdentity'
  import { useAdvancedKeyCommit } from '../composables/useAdvancedKeyCommit'

  const emit = defineEmits<{
    success: []
    cancel: []
  }>()

  const { t } = useLocaleStore()
  const { commitAdvancedKey } = useAdvancedKeyCommit()

  type KeyData = { keywords: string }

  const SINGLE_KEY_META_TYPE = 0x01
  const NODE_ANCHORS = [0, 0.31, 0.69, 1] as const
  const MAX_KEYS = 4
  const NODE_COUNT = 4
  // travels 单位 0.1mm；A=1.6mm=16, B=3.0mm=30, C=30, D=16（A<B 按下，D<C 释放镜像）
  const DKS_TRAVELS: [number, number, number, number] = [16, 30, 30, 16]
  const APPLY_TOAST_ID = 'advanced-dks-apply'

  const selectedKey = inject(SelectedKeySymbol)
  const keySettings = inject(KeySettingsSymbol)
  const currentLayer = inject(KeyLayerSymbol)

  const sliderRef = ref<InstanceType<typeof USlider> | null>(null)
  const sourceKeyboardEventNames: KeyEventName[] = ['click', 'dragstart']

  const nodeKeys = ref<KeyData[][]>(Array.from({ length: NODE_COUNT }, () => []))
  const activeNodeIndex = ref<number>(0)
  const coveredNodeIndexes = ref<number[]>([])
  const isSubmitting = ref(false)

  const isNodeCovered = (index: number) => coveredNodeIndexes.value.includes(index)

  const highlightedKeyCodes = computed(() => new Set(nodeKeys.value.flat().map((k) => k.keywords)))
  const hasAnyKey = computed(() => highlightedKeyCodes.value.size > 0)

  const isCanEnabled = computed(() =>
    Boolean(selectedKey?.value && hasAnyKey.value && !isSubmitting.value),
  )

  const handleCoveredChange = (indexes: number[]) => {
    coveredNodeIndexes.value = indexes
    if (isNodeCovered(activeNodeIndex.value)) {
      const next = Array.from({ length: NODE_COUNT }, (_, i) => i).find((i) => !isNodeCovered(i))
      activeNodeIndex.value = next ?? 0
    }
  }

  const restoreFromDksSlot = async (slot: DksSlotInfo) => {
    const restored: KeyData[][] = Array.from({ length: NODE_COUNT }, () => [])
    const progressValues: number[] = [...NODE_ANCHORS]

    // keys[K1-K4] 是按键池，每个 key 通过 state.segments[0/1/2].selected 和 state.final
    // 表示在段0(A→B) / 段1(B→C) / 段2(C→D) / 末位(D点) 是否触发。
    // 节点0→段0, 节点1→段1, 节点2→段2, 节点3→final
    for (const key of slot.keys) {
      if (!key || (key.type === 0 && key.codeL === 0 && key.codeH === 0)) continue

      const config = getKeyConfigByTypeCode(SINGLE_KEY_META_TYPE, key.codeH || key.codeL)
      if (!config?.keywords) continue

      for (let segIdx = 0; segIdx < 3; segIdx++) {
        if (key.state.segments[segIdx]?.selected) {
          if (!restored[segIdx].some((k) => k.keywords === config.keywords)) {
            restored[segIdx].push({ keywords: config.keywords })
          }
          // nextPointState 表示手柄拖到下一节点
          if (key.state.segments[segIdx].nextPointState && segIdx < NODE_COUNT - 1) {
            progressValues[segIdx] = NODE_ANCHORS[segIdx + 1] - 0.16
          }
        }
      }
      // 节点3 由 final 标记
      if (key.state.final) {
        if (!restored[3].some((k) => k.keywords === config.keywords)) {
          restored[3].push({ keywords: config.keywords })
        }
      }
    }

    nodeKeys.value = restored
    activeNodeIndex.value = 0

    await nextTick()
    sliderRef.value?.setProgress?.(progressValues)
  }

  watch(
    () => selectedKey?.value,
    (key) => {
      if (!key || !keySettings?.value) return

      const setting = findSettingByTargetKey(keySettings.value, key)
      if (setting?.keyType !== 'DKS') return

      const dksSlot = setting.raw?.dks as DksSlotInfo | undefined
      if (dksSlot?.keys) {
        void restoreFromDksSlot(dksSlot)
      }
    },
    { immediate: true },
  )

  watch(isCanEnabled, (enabled, wasEnabled) => {
    if (enabled && !wasEnabled) {
      toast.info(t('toast.keys.dksApply'), { id: APPLY_TOAST_ID })
    }
  })

  const keyboardConfig = computed<KeyboardConfig | null>(() => {
    return {
      ...fullSize108,
      keys: fullSize108.keys.map((row) =>
        row.map((key) => {
          const { w, h, x, y, keywords, ...rest } = key
          const config: KeyConfig = KEYCODE_KEYWORDS_MAP.get(keywords)!

          return {
            ...rest,
            ...config,
            rect: { w, h, x, y },
          }
        }),
      ),
    }
  })

  const handleSourceKeyEvent = (ctx: KeyboardKeyEventPayload) => {
    const { data, eventName, nativeEvent } = ctx

    if (eventName === 'click') {
      if (isNodeCovered(activeNodeIndex.value)) return

      const keywords = data.keywords
      const activeNode = nodeKeys.value[activeNodeIndex.value]
      if (activeNode.some((k) => k.keywords === keywords)) return
      if (highlightedKeyCodes.value.size >= MAX_KEYS && !highlightedKeyCodes.value.has(keywords))
        return
      activeNode.push(data)
    }

    if (eventName === 'dragstart') {
      const dragEvent = nativeEvent as DragEvent
      if (!dragEvent.dataTransfer) return
      setDragData(dragEvent.dataTransfer, DragType.KEY_MAPPING, data as any)
    }
  }

  const handleNodeClick = (index: number) => {
    if (isNodeCovered(index)) return
    activeNodeIndex.value = index
  }

  const handleNodeDrop = (payload: { nodeIndex: number; keyData: KeyData }) => {
    const { nodeIndex, keyData } = payload
    if (isNodeCovered(nodeIndex)) return

    const node = nodeKeys.value[nodeIndex]
    if (node.some((k) => k.keywords === keyData.keywords)) return
    if (
      highlightedKeyCodes.value.size >= MAX_KEYS &&
      !highlightedKeyCodes.value.has(keyData.keywords)
    )
      return
    node.push(keyData)
    activeNodeIndex.value = nodeIndex
  }

  const handleNodeRemove = (payload: { nodeIndex: number; keyIndex: number }) => {
    nodeKeys.value[payload.nodeIndex].splice(payload.keyIndex, 1)
  }

  const emptyContactState = (): DksContactState => ({
    segments: [
      { selected: false, continuous: false, nextPointState: false },
      { selected: false, continuous: false, nextPointState: false },
      { selected: false, continuous: false, nextPointState: false },
    ],
    final: false,
  })

  const getDragTargetNode = (handleIdx: number): number | null => {
    const slider = sliderRef.value
    const progressValues = slider?.progress ?? []
    const anchors: readonly number[] = slider?.NODE_ANCHORS ?? NODE_ANCHORS
    const currentP = progressValues[handleIdx] ?? anchors[handleIdx]
    const anchorP = anchors[handleIdx]
    if (Math.abs(currentP - anchorP) < 0.02) return null
    for (let j = handleIdx + 1; j < anchors.length; j++) {
      if (Math.abs(currentP - (anchors[j] - 0.16)) < 0.02) return j
    }
    for (let j = handleIdx + 1; j < anchors.length; j++) {
      if (currentP < anchors[j]) return j
    }
    return null
  }

  /**
   * 从 nodeKeys + slider 状态构造 DksSlotInfo.keys（最多4个 DksKeyInfo）。
   * 协议：keys[K1-K4] 是按键池，每个 key 的 state 描述在哪些段触发。
   * 节点0→段0(A→B)，节点1→段1(B→C)，节点2→段2(C→D)，节点3→state.final(D点)。
   * 同一个按键出现在多个节点时合并到同一 key slot 并合并 state。
   */
  const buildDksKeys = (): DksSlotInfo['keys'] => {
    // 按 keywords 收集各节点信息，合并到同一 key slot
    const keyMap = new Map<
      string,
      { triple: ReturnType<typeof keyConfigToGcomeTriple>; state: DksContactState }
    >()

    for (let nodeIdx = 0; nodeIdx < NODE_COUNT; nodeIdx++) {
      for (const keyData of nodeKeys.value[nodeIdx]) {
        const config = KEYCODE_KEYWORDS_MAP.get(keyData.keywords)
        if (!config) continue

        if (!keyMap.has(keyData.keywords)) {
          keyMap.set(keyData.keywords, {
            triple: keyConfigToGcomeTriple(config as any),
            state: emptyContactState(),
          })
        }

        const entry = keyMap.get(keyData.keywords)!
        if (nodeIdx === 3) {
          // 节点3：末位 final
          entry.state.final = true
        } else {
          // 节点0/1/2 → 段0/1/2
          const dragged = getDragTargetNode(nodeIdx) !== null
          entry.state.segments[nodeIdx] = {
            selected: true,
            continuous: false,
            nextPointState: dragged,
          }
        }
      }
    }

    const keys: DksKeyInfo[] = []
    for (const { triple, state } of keyMap.values()) {
      if (keys.length >= MAX_KEYS) break
      keys.push({ type: triple.type, codeL: triple.codeL, codeH: triple.codeH, state })
    }
    // 补齐4个
    while (keys.length < MAX_KEYS) {
      keys.push({ type: 0, codeL: 0, codeH: 0, state: emptyContactState() })
    }

    return keys as DksSlotInfo['keys']
  }

  const resolveTargetIndex = () => {
    if (!selectedKey?.value || !keySettings?.value) return undefined
    const existingSetting = findSettingByTargetKey(keySettings.value, selectedKey.value)
    return parseNumber(existingSetting?.raw?.index) ?? parseNumber(selectedKey.value.key)
  }

  const handleTakeEffect = async () => {
    if (!hasAnyKey.value) {
      toast.info(t('toast.keys.dksSelectKeyFirst'))
      return
    }

    if (!selectedKey?.value) {
      toast.info(t('toast.keys.dksSelect'))
      return
    }

    const targetIndex = resolveTargetIndex()
    if (targetIndex == null) return

    const existingSetting = keySettings?.value
      ? findSettingByTargetKey(keySettings.value, selectedKey.value)
      : undefined

    const dksKeys = buildDksKeys()
    const dks: DksSlotInfo = {
      index: 0,
      travels: DKS_TRAVELS,
      keys: dksKeys,
    }

    const dksKeyLabels = nodeKeys.value
      .map((nodeList) => {
        const config = nodeList[0] ? KEYCODE_KEYWORDS_MAP.get(nodeList[0].keywords) : undefined
        return config ? getLocalizedField(config.alias || config.name) : ''
      })
      .filter(Boolean)
      .join('/')

    const payload: KeyChangePayload = {
      keywords: selectedKey.value.keywords,
      keyType: 'DKS',
      target: null,
      raw: {
        type: GCOME_MATRIX_TYPE.DKS,
        dks,
        dksKeyLabels,
      },
    }

    const newSetting = buildKeySettingFromPayload({
      sourceData: payload,
      index: targetIndex,
    })
    if (!newSetting) return

    if (existingSetting?.raw?.dksIndex != null) {
      newSetting.raw.dksIndex = existingSetting.raw.dksIndex
    }

    let didSucceed = false
    isSubmitting.value = true
    toast.dismiss(APPLY_TOAST_ID)
    try {
      const committed = await commitAdvancedKey({
        setting: newSetting,
        existingSetting,
        isFn: currentLayer?.value === 'fn',
      })
      if (!committed) return

      toast.dismiss('keyboard')
      emit('success')
      didSucceed = true
    } finally {
      if (!didSucceed) isSubmitting.value = false
    }
  }

  const getKeyClass = (config: KeyConfig) => {
    const base =
      'flex items-center justify-center gap-1 bg-key font-[Barlow] text-xs-semibold! whitespace-nowrap transition-colors cursor-pointer disabled:text-text-glass-disabled disabled:cursor-not-allowed disabled:pointer-events-none'
    if (highlightedKeyCodes.value.has(config.keywords)) {
      return `${base} bg-white text-brand-blue2 hover:text-brand-blue2`
    }
    return `${base} text-black-white hover:text-text-glass-default active:bg-white active:text-brand-blue2`
  }

  const getKeyDisabled = (config: KeyConfig): boolean => {
    if (highlightedKeyCodes.value.size < MAX_KEYS) return false
    return !highlightedKeyCodes.value.has(config.keywords)
  }
</script>

<template>
  <div class="w-full flex items-center justify-center gap-2">
    <div class="flex flex-col glass-frosted rounded-8xl w-83 h-67.5 shrink-0 p-4">
      <div class="flex items-start justify-between gap-3 h-10 shrink-0">
        <Button color="normal" class="w-12 h-6" size="sm" @click="emit('cancel')">
          {{ $t('common.backButton') }}
        </Button>
        <div class="flex-1 h-6 flex justify-start items-center">
          <Tooltip :content="$t('keys.dks.intro')">
            <Icon icon="Function-Wireframe-Description" class="size-4 text-text-glass-hover" />
          </Tooltip>
        </div>
        <!-- <div class="flex items-center gap-1 text-text-glass-default flex-1 min-w-0">
          <Icon icon="Function-FiII-Description" class="w-4 h-4 shrink-0" />
          <span class="text-s-medium truncate">{{ $t('keys.dks.intro') }}</span>
        </div> -->
        <Button
          color="glass"
          class="w-12 h-6"
          size="sm"
          :active="isCanEnabled"
          :disabled="!isCanEnabled"
          @click="handleTakeEffect"
        >
          {{ $t('keys.dks.apply') }}
        </Button>
      </div>
      <div class="flex-1">
        <USlider
          ref="sliderRef"
          :node-keys="nodeKeys"
          :active-node-index="activeNodeIndex"
          @node-click="handleNodeClick"
          @node-drop="handleNodeDrop"
          @node-remove="handleNodeRemove"
          @covered-change="handleCoveredChange"
        />
      </div>
    </div>

    <div class="glass-frosted relative rounded-8xl w-250 h-67.5 p-4">
      <div class="p-2">
        <Keyboard
          :keyboard-config="keyboardConfig!"
          :native-event-names="sourceKeyboardEventNames"
          :key-class="getKeyClass"
          :key-disabled="getKeyDisabled"
          :gap="6"
          @key-event="handleSourceKeyEvent"
        />
      </div>
    </div>
  </div>
</template>

<style scoped></style>
